import * as routeNames from '@/router/routeNames';
import {
  CustomSelectSuburb,
  JBAdvancedStopProducts,
  JBAdvancedWHStopProductsFull,
  JBGoodsTableDataFullFixed,
} from '@/models/jbAdvanced';
// LEGACY IMPORTS
import {
  JobDetailedData as JobDetailedDataLegacy,
  JobDetailedStop as JobDetailedStopLegacy,
} from '@/models/jobDetailed';

import { AddressBookLookupData } from '@/models/addressBook';
import { JobProducts } from '@/models/jobData';
import { QuantList } from '@/models/jobQuants';
import { Product as ProductLegacy } from '@/models/jobDetailed';
import { ReferencesParams } from '@/models/main';
import { StopServices } from '@/models/jobDetailed';
import Vue from 'vue';
import VueRouter from 'vue-router';
import _uniqueId from 'lodash/uniqueId';
import { components } from '@/models/jobDetailed';
import { fetchReferencesParams } from '@/helpers/fetchers';
import { notifyNetworkError } from './notifiers';
import { sendError } from './sentry';

type JobDetailedData = components['schemas']['Job'];
type JobDetailedStop = components['schemas']['Stop'];
type Product = components['schemas']['Product'];

export function getReferencesParamsWrapper(customerCode: string, data): Promise<void> {
  return fetchReferencesParams(customerCode)
    .then((referencesParams: ReferencesParams) => {
      data.ref1_prompt = referencesParams.ref1_prompt || 'Reference 1';
      data.ref2_prompt = referencesParams.ref2_prompt || 'Reference 2';
      data.ref3_prompt = referencesParams.ref3_prompt || 'Reference 3';
      data.ref1_required = referencesParams.ref1_required || false;
      data.ref2_required = referencesParams.ref2_required || false;
      data.ref3_required = referencesParams.ref3_required || false;
    })
    .catch(err => {
      sendError(err, 'FetchReferencesParams');
    });
}

export function getSuburbItem(suburb: string, postcode: string, state: string): CustomSelectSuburb {
  let suburbId = [suburb, postcode, state].join('|');
  if (suburbId === '||') {
    suburbId = '';
  }
  return {
    id: suburbId,
    name: suburb,
    postcode: postcode,
    state: state,
  };
}

export function cloneDetailedJob(initialDetailedJob: JobDetailedDataLegacy): JobDetailedDataLegacy {
  const detailedJob = structuredClone(initialDetailedJob);
  detailedJob.stops.forEach(stop => {
    stop.pickup_products.forEach((pickupProduct: ProductLegacy) => {
      pickupProduct._clientId = _uniqueId();
    });
    stop.services.forEach((service: StopServices) => {
      service._clientId = _uniqueId();
    });
    if (!stop.note_text) {
      stop.note_text = '';
    }
    if (!stop.note_name) {
      stop.note_name = '';
    }
    if (!stop.note_phone) {
      stop.note_phone = '';
    }
    if (!stop.note_email) {
      stop.note_email = '';
    }
  });
  return detailedJob;
}

export function uncloneDetailedJob(clonedDetailedJob: JobDetailedDataLegacy): JobDetailedDataLegacy {
  const detailedJob = structuredClone(clonedDetailedJob);
  detailedJob.stops.forEach(stop => {
    stop.pickup_products.forEach((pickupProduct: ProductLegacy) => {
      delete pickupProduct._clientId;
    });
    stop.services.forEach((service: StopServices) => {
      delete service._clientId;
    });
  });
  return detailedJob;
}

// every `time_earliest` and every `time_latest` must exist
// the current `time_earliest` must be less then the current `time_latest`
// the current `time_earliest` must be less then the next `time_latest`
export function getIsDatesError(stops: JobDetailedStopLegacy[]): boolean {
  let isDatesError = false;
  stops.forEach((stop, index) => {
    if (isDatesError) {
      return;
    }
    const curTimeEarliest = new Date(stop.time_earliest).getTime();
    const curTimeLatest = new Date(stop.time_latest).getTime();
    if (!curTimeEarliest || !curTimeLatest || curTimeEarliest >= curTimeLatest) {
      isDatesError = true;
    }
    if (isDatesError) {
      return;
    }
    const nextStop = stops[index + 1];
    if (!nextStop) {
      return;
    }
    const nextTimeLatest = new Date(nextStop.time_latest).getTime();
    if (!nextTimeLatest || curTimeEarliest >= nextTimeLatest) {
      isDatesError = true;
    }
  });
  return isDatesError;
}

const prefixList = ['OUT: RECYCLE: ', 'OUT: STORE: ', 'OUT: RETURN: ', 'OUT: WORKSHOP: ', 'OUT: ', 'IN: '];
const prefix = new RegExp('^(' + prefixList.join('|') + ')', 'g');

export function getPrefixFromString(value: string): string {
  const afterLastPrefix = value.replace(prefix, '');
  if (afterLastPrefix === '') {
    return value;
  }
  return value.split(afterLastPrefix)[0];
}

export function getModelFromString(model: string) {
  return model.replace(prefix, '');
}

/**
 * Prepares job data for a warehouse to warehouse booking.
 *
 * @param job - The job data from JobDetailed
 * @returns An object containing:
 *  - userData: Information about the user, including customer code, caller, phone number, and references.
 *  - stops: An array of addresses from the AddressBook
 *  - products: An array of products from the Product list
 *  - no date
 */
function createJobForWhToWhBooking(job: JobDetailedData | JobDetailedDataLegacy) {
  const userData = {
    customerCode: job.customer,
    caller: job.caller,
    phone_number: job.phone,
    reference1: job.reference_1,
    reference2: job.reference_2,
    reference3: job.reference_3,
    reference4: job.reference_4,
    ref1_prompt: job.customer_ref1_prompt,
    ref2_prompt: job.customer_ref2_prompt,
    ref3_prompt: job.customer_ref3_prompt,
    ref4_prompt: job.customer_ref4_prompt,
  };
  const stops: AddressBookLookupData[] = job.stops.map((stop, idx) => {
    return {
      id: idx,
      city: '',
      line1: stop.address_line_1,
      line2: stop.address_line_2 || '',
      line3: '',
      warehouse: true,
      name: stop.name,
      suburb: {
        id: -1,
        name: stop.suburb,
        state: stop.state,
        postcode: stop.post_code,
      },
      note_text: stop.note_text || '',
      note_name: stop.note_name || '',
      note_phone: stop.note_phone || '',
    };
  });
  const products: JobProducts[] = Object.values(job.stops[0].pickup_products).map((product, idx) => {
    const lastIndex = product.model?.lastIndexOf(': ');
    const productName = lastIndex && lastIndex >= 0 ? product.model?.substring(lastIndex + 2) : product.model;
    return {
      id: idx,
      model: productName || '',
      model_id: product.model_id || -1,
      description: product.description || '',
      serial: product.serial || '',
      quantity: product.pieces || 1,
      from_stop: 1,
      to_stop: 2,
      runup: product.extra_options?.runup || false,
      returns: product.extra_options?.returns || '',
      extra_options: {
        runup: product.extra_options?.runup || false,
        returns: product.extra_options?.returns || '',
        runup_tasks: product.extra_options?.runup_tasks?.map(task => task.comment).join(', ') || '',
        installation: product.extra_options?.installation || 'DLVONLY',
      },
      installation: 'DLVONLY',
      weight: product.weight || 0,
      quant_id: Number(product.quant_id) || null,
    };
  });
  return {
    userData,
    stops,
    products,
  };
}

/**
 * Clones a job and navigates to the appropriate booking route based on the job's stop details.
 * The function checks if the job is a warehouse to warehouse booking or a warehouse to same warehouse booking.
 * If so, it stores the job data in local storage and routes to the corresponding page.
 * Otherwise, it maps job details to userData, stops, and products, stores them in local storage,
 * and routes to the AdvancedJB page.
 *
 * TODO: How to handle BreakFix jobs?
 *
 * @param job - The job data from Detailed Job page.
 * @param router - The router instance for navigation.
 */
export function cloneJob(job: JobDetailedData | JobDetailedDataLegacy, router: VueRouter): void {
  try {
    const isWhToWhF =
      job.stops.length === 2 &&
      job.stops[0]?.is_warehouse &&
      job.stops[1]?.is_warehouse &&
      job.stops[0].name !== job.stops[1].name;
    const isWhToSameWhF =
      job.stops.length === 2 &&
      job.stops[0]?.is_warehouse &&
      job.stops[1]?.is_warehouse &&
      job.stops[0].name === job.stops[1].name;

    if (isWhToWhF) {
      const newJob = createJobForWhToWhBooking(job);
      const localStorageJobData = {
        userData: newJob.userData,
        stops: newJob.stops,
        products: newJob.products,
      };
      localStorage.setItem('jobDataWHToWH', JSON.stringify(localStorageJobData));
      router.push({ name: routeNames.WAREHOUSE_TO_WH_JB });
      return;
    }

    if (isWhToSameWhF) {
      const newJob = createJobForWhToWhBooking(job);
      const localStorageJobData = {
        userData: newJob.userData,
        stops: newJob.stops,
        products: newJob.products,
      };
      localStorage.setItem('jobDataWHSameWH', JSON.stringify(localStorageJobData));
      router.push({ name: routeNames.WAREHOUSE_SAME_WH_JB });
      return;
    }

    const userData = {
      customerCode: job.customer,
      caller: job.caller,
      phone_number: job.phone,
      reference1: job.reference_1,
      reference2: job.reference_2,
      reference3: job.reference_3,
      reference4: job.reference_4,
      ref1_prompt: job.customer_ref1_prompt,
      ref2_prompt: job.customer_ref2_prompt,
      ref3_prompt: job.customer_ref3_prompt,
      ref4_prompt: job.customer_ref4_prompt,
    };

    const stops = job.stops.map((stop, idx) => {
      return {
        id: idx,
        suburb: stop.suburb,
        suburbId: -1,
        state: stop.state,
        postcode: stop.post_code,
        notesPhone: stop.note_phone,
        notesName: stop.note_name,
        notesInstruction: stop.note_text,
        warehouse: stop.is_warehouse,
        addressBookCheckbox: false,
        name: stop.name,
        address_1: stop.address_line_1,
        address_2: stop.address_line_2,
      };
    });

    const returnNames = {
      RECYCLE: 'Recycle',
      STORE: 'Store',
      RETURN: 'Return',
      WORKSHOP: 'Workshop',
    };

    const goods1: JBAdvancedWHStopProductsFull[] = Object.values(job.stops[0].pickup_products).map((product, idx) => {
      const lastIndex = product.model?.lastIndexOf(': ');
      const productName = lastIndex && lastIndex >= 0 ? product.model?.substring(lastIndex + 2) : product.model;
      return {
        id: idx,
        product_id: product.model_id,
        model: productName,
        description: product.description,
        serial_number: product.serial,
        serialNumberFilled: !!product.serial,
        statusText: '',
        quantity: product.pieces,
        from_stop: 1,
        to_stop: 2,
        weight: product.weight,
        width: product.width,
        height: product.height,
        length: product.length,
        runup: product.extra_options ? product.extra_options.runup : false,
        returnOption: '',
        returnOptionText: '',
        quant_id: product.quant_id,
        installation:
          product.extra_options && product.extra_options.installation ? product.extra_options.installation : '',
        ...(product.extra_options &&
          product.extra_options.returns && { returnOption: returnNames[product.extra_options.returns] }),
      };
    });
    let goods2: JBAdvancedWHStopProductsFull[] | null = null;
    if (Object.keys(job.stops[1].pickup_products).length) {
      goods2 = Object.values(job.stops[1].pickup_products).map((product, idx) => {
        const lastIndex = product.model?.lastIndexOf(': ');
        const productName = lastIndex && lastIndex >= 0 ? product.model?.substring(lastIndex + 2) : product.model;
        return {
          id: idx,
          product_id: product.model_id,
          model: productName,
          description: product.description,
          serial_number: product.serial,
          serialNumberFilled: !!product.serial,
          statusText: '',
          quantity: product.pieces,
          from_stop: 2,
          to_stop: 3,
          weight: product.weight,
          width: product.width,
          height: product.height,
          length: product.length,
          runup: product.extra_options ? product.extra_options.runup : false,
          returnOption: '',
          returnOptionText: '',
          quant_id: product.quant_id,
          installation:
            product.extra_options && product.extra_options.installation ? product.extra_options.installation : '',
          ...(product.extra_options &&
            product.extra_options.returns && { returnOption: returnNames[product.extra_options.returns] }),
        };
      });
    }
    let readFromIdx = 0;
    if (job.stops[0].is_warehouse && !job?.stops[2]?.is_warehouse) {
      readFromIdx = 1;
    }
    if (!job.stops[0].is_warehouse && job?.stops[1]?.is_warehouse) {
      readFromIdx = 0;
    }
    if (job.stops[0].is_warehouse && job?.stops[2]?.is_warehouse) {
      readFromIdx = 1;
    }
    const goods: JBGoodsTableDataFullFixed[] = [
      {
        tableData: goods1,
        checkboxStairs: job.stops[readFromIdx].attributes.indexOf('SSC') >= 0,
        checkboxLoadingDock: job.stops[readFromIdx].attributes.indexOf('LOW') >= 0,
        checkboxPPE: job.stops[readFromIdx].attributes.indexOf('PPE') >= 0,
        checkboxRunup: job.stops[0].pickup_products[0]?.extra_options?.runup || false,
        radioTypeGoods: 'radio',
        stairsNotes: '',
      },
    ];
    if (goods2) {
      goods.push({
        tableData: goods2,
        checkboxStairs: false,
        checkboxLoadingDock: false,
        checkboxPPE: false,
        checkboxRunup: false,
        radioTypeGoods: '',
        stairsNotes: '',
        runup: false,
      });
    }

    const localStorageJobData = {
      userData: userData,
      stops: stops,
      goods: goods,
    };
    localStorage.setItem('jobData', JSON.stringify(localStorageJobData));
    router.push({ name: routeNames.ADVANCED_JOB_BOOKING });
  } catch {
    notifyNetworkError({ text: 'Error when cloning jobs' });
  }
}

export function scrollToFirstError(
  this: Vue & { $vuetify: { goTo: (el: HTMLElement, options?: { offset?: number }) => void } },
): void {
  this.$nextTick(() => {
    const el = this.$el.querySelector('.v-messages.error--text:first-of-type') as HTMLElement | null;
    if (el) {
      this.$vuetify.goTo(el, { offset: 100 });
    }
  });
}

export async function createJobForAdvancedBooking(
  productsToBook: QuantList[],
  chosenWhData: AddressBookLookupData | undefined,
  customerCode: string,
  ricohWarehouseAddress: AddressBookLookupData,
) {
  // Fill only CC
  const userData = {
    customerCode: customerCode,
    caller: '',
    phone_number: '',
    reference1: '',
    reference2: '',
    reference3: '',
    ref1_prompt: '',
    ref2_prompt: '',
    ref3_prompt: '',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
  };
  // Fill only first stop as we know the WH
  const stops = [
    {
      id: 0,
      suburb: chosenWhData?.suburb.name,
      suburbId: chosenWhData?.suburb.id,
      state: chosenWhData?.suburb.state,
      postCode: chosenWhData?.suburb.postcode,
      notesPhone: '',
      notesName: '',
      notesInstruction: '',
      warehouse: true,
      addressBookCheckbox: false,
      name: chosenWhData?.name,
      address_1: chosenWhData?.line1,
      address_2: chosenWhData?.line2,
    },
    {
      id: 1,
      suburb: '',
      suburbId: -1,
      state: '',
      postCode: '',
      notesPhone: '',
      notesName: '',
      notesInstruction: '',
      warehouse: false,
      addressBookCheckbox: false,
    },
  ];
  // Set RICOH WH address if products are chosen there
  if (productsToBook[0].warehouse_code === 'RIC') {
    stops[0] = {
      id: 0,
      suburb: ricohWarehouseAddress?.suburb.name,
      suburbId: ricohWarehouseAddress?.suburb.id,
      state: ricohWarehouseAddress?.suburb.state,
      postCode: ricohWarehouseAddress?.suburb.postcode,
      notesPhone: '',
      notesName: '',
      notesInstruction: '',
      warehouse: true,
      addressBookCheckbox: false,
      name: ricohWarehouseAddress?.name,
      address_1: ricohWarehouseAddress.line1 || '',
      address_2: ricohWarehouseAddress.line2 || '',
    };
  }

  const goods: Partial<JBAdvancedStopProducts>[] = productsToBook.map((x, index) => {
    const model = x.product_id[1].split('] ')[1] || x.product_id[1] || '';
    const descriptionParts: string[] = [];
    if (x.reference) {
      descriptionParts.push(x.reference);
    }
    if (x.reference2) {
      descriptionParts.push(x.reference2);
    }
    const description = descriptionParts.join(' / ');
    return {
      id: index,
      product_id: x.product_id[0],
      model: model,
      description: description,
      serial_number: x.lot_id?.[1] || '',
      serialNumberFilled: !!x.lot_id?.[1],
      statusText: 'Quant ID: ' + x.id,
      quantity: x.qty,
      from_stop: 1,
      to_stop: 2,
      weight: 0,
      runup: false,
      quant_id: x.id,
      installation: 'DLVONLY',
      returnOption: '',
      returnOptionText: '',
    };
  });
  const localStorageJobData = {
    userData: userData,
    stops: stops,
    goods: [
      {
        tableData: goods,
        checkboxStairs: false,
        checkboxLoadingDock: false,
        checkboxPPE: false,
        checkboxRunup: false,
        radioTypeGoods: '',
        stairsNotes: '',
        runup: false,
      },
    ],
  };
  return localStorageJobData;
}

export async function createJobForWarehouse(
  cart: QuantList[],
  customerCode: string,
  chosenWhData: AddressBookLookupData | undefined,
) {
  const userData = {
    customerCode: customerCode,
    caller: '', // fill with user data?
    phone_number: '', // fill with user data?
    reference1: cart[0].reference || '',
    reference2: cart[0].reference2 || '',
    reference3: '',
    ref1_prompt: '',
    ref2_prompt: '',
    ref3_prompt: '',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
  };

  let stops: AddressBookLookupData[] = [];
  stops = [
    {
      ...chosenWhData,
      id: 0,
      name: chosenWhData?.name || '',
      city: chosenWhData?.city || '',
      line1: chosenWhData?.line1 || '',
      line2: chosenWhData?.line2 || '',
      line3: chosenWhData?.line2 || '',
      suburb: {
        id: chosenWhData?.suburb?.id || -1,
        name: chosenWhData?.suburb?.name || '',
        postcode: chosenWhData?.suburb?.postcode || '',
        state: chosenWhData?.suburb?.state || '',
      },
      warehouse: false,
      note_text: '',
      note_name: '',
      note_phone: '',
    },
    {
      ...chosenWhData,
      id: 1,
      name: chosenWhData?.name || '',
      city: chosenWhData?.city || '',
      line1: chosenWhData?.line1 || '',
      line2: chosenWhData?.line2 || '',
      line3: chosenWhData?.line3 || '',
      suburb: {
        id: chosenWhData?.suburb?.id || -1,
        name: chosenWhData?.suburb?.name || '',
        postcode: chosenWhData?.suburb?.postcode || '',
        state: chosenWhData?.suburb?.state || '',
      },
      warehouse: false,
      note_text: '',
      note_name: '',
      note_phone: '',
    },
  ];

  const products: JobProducts[] = cart.map(product => {
    const descriptionParts: string[] = [];
    if (product.reference) {
      descriptionParts.push(product.reference);
    }
    if (product.reference2) {
      descriptionParts.push(product.reference2);
    }
    const description = descriptionParts.join(' / ');
    return {
      model: product.product_id[1].split('] ')[1] || '',
      model_id: product.product_id[0],
      description: description,
      serial: String(product.lot_id?.[1] || ''),
      quantity: product.qty,
      from_stop: 1,
      to_stop: 2,
      weight: 0,
      runup: false,
      quant_id: product.id,
      installation: 'DLVONLY',
      returns: '',
    };
  });

  const localStorageJobData = {
    userData: userData,
    stops: stops,
    products: products,
  };

  return localStorageJobData;
}
